import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Paper,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Send as SendIcon,
  Timeline as TimelineIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
  FolderOpen as CaseIcon,
  Home as HomeIcon,
  BugReport as DetectionIcon,
  Visibility as ViewIcon,
  LinkOff as UnlinkIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Group as TeamIcon,
  Label as TagIcon,
  CheckCircle as ResolvedIcon,
  Inventory as EvidenceIcon,
  Fingerprint as IndicatorIcon,
  Devices as EntityIcon,
  AutoAwesome as AIIcon,
  OpenInNew as OpenInNewIcon,
  MenuBook as PlaybookIcon,
  Checklist as TasksIcon,
  Timer as SlaIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Description as ReportIcon,
  Link as JiraIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { usePermission } from '../hooks/usePermission';
import useAiStatus from '../hooks/useAiStatus';
import { formatDateTime, formatRelativeTime } from '../utils/dateUtils';
import { DetectionDetail } from '../components/DetectionDetail';
import { SlaBadge, minutesToHuman, toDate, formatDuration } from '../components/Cases/SlaBadge';
import { TaskList } from '../components/Cases/TaskList';
import { PlaybookPicker } from '../components/Cases/PlaybookPicker';
import AskAiPanel, { renderMarkdown } from '../components/AI/AskAiPanel';
import InvestigationTimeline from '../components/Cases/InvestigationTimeline';

const DEFAULT_SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];
const DEFAULT_STATUSES = ['Open', 'In Progress', 'Under Review', 'Resolved', 'Closed'];
const DEFAULT_TEAM_ROLES = ['Lead', 'Analyst', 'Reviewer', 'Observer'];
const EVIDENCE_TYPES = ['Log', 'File', 'Screenshot', 'Network Capture', 'Memory Dump', 'Email', 'Document', 'Other'];
const RESOLVED_STATUSES = ['Resolved', 'Closed'];

const userName = (u, fallback = 'Unassigned') => {
  if (!u) return fallback;
  if (typeof u === 'string') return fallback;
  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  return name || u.username || fallback;
};

const initials = (u) => {
  if (!u || typeof u === 'string') return 'S';
  return `${u.firstName?.[0] || ''}${u.lastName?.[0] || ''}` || u.username?.[0]?.toUpperCase() || 'U';
};

const refId = (v) => (v && typeof v === 'object' ? v._id : v);

const emptyResolution = { summary: '', rootCause: '', actionsTaken: '', lessonsLearned: '' };
const emptyEvidence = { type: 'Log', name: '', description: '' };

export const CaseDetailPage = () => {
  const { id: caseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getSeverityColor, getStatusColor, getFormattedStatuses } = useSettings();
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission('cases', 'update');
  const canDelete = hasPermission('cases', 'delete');
  const aiStatus = useAiStatus();

  // Core state
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [users, setUsers] = useState([]);
  const [options, setOptions] = useState({
    statuses: DEFAULT_STATUSES,
    severities: DEFAULT_SEVERITIES,
    categories: [],
    priorities: DEFAULT_SEVERITIES,
    teamRoles: DEFAULT_TEAM_ROLES,
  });

  // Comments
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  // Case management
  const [editingCaseManagement, setEditingCaseManagement] = useState(false);
  const [caseManagementForm, setCaseManagementForm] = useState({
    status: '',
    assignedTo: '',
    severity: '',
    priority: '',
    category: '',
  });
  const [savingCaseManagement, setSavingCaseManagement] = useState(false);

  // Generic mutation flag (tags/team/evidence/resolution/detections)
  const [mutating, setMutating] = useState(false);

  // Tasks / playbooks
  const [playbookPickerOpen, setPlaybookPickerOpen] = useState(false);
  const [applyingPlaybook, setApplyingPlaybook] = useState(false);
  const [playbookSuggestion, setPlaybookSuggestion] = useState(null);
  const [suggestionVisible, setSuggestionVisible] = useState(false);
  const [suggestingPlaybook, setSuggestingPlaybook] = useState(false);

  // AI incident report
  const [generatingReport, setGeneratingReport] = useState(false);

  // Detections
  const [detectionModalOpen, setDetectionModalOpen] = useState(false);
  const [selectedDetectionId, setSelectedDetectionId] = useState(null);
  const [detectionSearch, setDetectionSearch] = useState('');
  const [detectionOptions, setDetectionOptions] = useState([]);
  const [loadingDetections, setLoadingDetections] = useState(false);
  const [detectionToAdd, setDetectionToAdd] = useState(null);

  // Team
  const [teamUser, setTeamUser] = useState(null);
  const [teamRole, setTeamRole] = useState('Analyst');

  // Tags
  const [newTag, setNewTag] = useState('');

  // Resolution dialog
  const [resolutionDialogOpen, setResolutionDialogOpen] = useState(false);
  const [resolutionForm, setResolutionForm] = useState(emptyResolution);
  const [pendingStatus, setPendingStatus] = useState(null);

  // Evidence
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  const [evidenceForm, setEvidenceForm] = useState(emptyEvidence);

  // Delete
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // External tickets (Jira / ServiceNow)
  const [jiraBusy, setJiraBusy] = useState(false);
  const [jiraActionError, setJiraActionError] = useState('');
  const [snowEnabled, setSnowEnabled] = useState(false);
  const [snowBusy, setSnowBusy] = useState(false);

  const statusOptions = useMemo(() => {
    const formatted = getFormattedStatuses('case');
    if (formatted && formatted.length > 0) return formatted;
    return options.statuses.map((s) => ({ value: s, label: s }));
  }, [getFormattedStatuses, options.statuses]);

  const formFromCase = (c) => ({
    status: c.status || '',
    assignedTo: refId(c.assignedTo) || '',
    severity: c.severity || '',
    priority: c.priority || '',
    category: c.category || '',
  });

  const fetchCase = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await apiService.cases.getById(caseId);
      const c = response.data.case || response.data;
      setCaseData(c);
      setCaseManagementForm(formFromCase(c));
      setError('');
    } catch (err) {
      console.error('Error fetching case:', err);
      setError(err.response?.data?.message || 'Failed to load case details');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (!caseId) return;
    fetchCase();
    apiService.users.getAll({ limit: 100 })
      .then((res) => setUsers(res.data.users || []))
      .catch((err) => console.error('Error fetching users:', err));
    apiService.cases.getOptions()
      .then((res) => { if (res.data) setOptions((prev) => ({ ...prev, ...res.data })); })
      .catch((err) => console.error('Error fetching case options:', err));
    // ServiceNow controls only appear when the tenant has the integration turned on.
    apiService.integrations.getServiceNow()
      .then((res) => setSnowEnabled(Boolean(res.data?.servicenow?.enabled)))
      .catch(() => setSnowEnabled(false));
  }, [caseId, fetchCase]);

  // Search unlinked detections for the "Add detection" picker
  useEffect(() => {
    if (!canUpdate) return undefined;
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        setLoadingDetections(true);
        const params = { limit: 100, sort: '-createdAt' };
        if (detectionSearch.trim()) params.search = detectionSearch.trim();
        const res = await apiService.detections.getAll(params);
        if (cancelled) return;
        const linkedIds = new Set((caseData?.detections || []).map((d) => String(refId(d))));
        setDetectionOptions((res.data.detections || []).filter((d) => !d.case && !linkedIds.has(String(d._id))));
      } catch (err) {
        if (!cancelled) console.error('Error searching detections:', err);
      } finally {
        if (!cancelled) setLoadingDetections(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [detectionSearch, caseData?.detections, canUpdate]);

  const formatCaseDateTime = (dateString) => formatDateTime(dateString, user?.preferences);

  const showError = (err, fallback) => {
    console.error(fallback, err);
    const data = err?.response?.data;
    const validation = Array.isArray(data?.errors) ? data.errors.map((e) => e.msg || e.message).filter(Boolean).join('; ') : '';
    setError(data?.message || validation || fallback);
  };

  const handleTasksChanged = (tasks) => setCaseData((prev) => (prev ? { ...prev, tasks } : prev));

  const handleApplyPlaybook = async (playbookId) => {
    if (!playbookId) return;
    try {
      setApplyingPlaybook(true);
      const res = await apiService.caseTasks.applyPlaybook(caseId, playbookId);
      setPlaybookPickerOpen(false);
      setNotice(res.data?.message || `Playbook applied (${res.data?.added ?? 0} tasks added)`);
      await fetchCase({ silent: true });
    } catch (err) {
      showError(err, 'Failed to apply playbook');
    } finally {
      setApplyingPlaybook(false);
    }
  };

  // ---------- AI: playbook suggestion ----------
  const handleSuggestPlaybook = async () => {
    try {
      setSuggestingPlaybook(true);
      setError('');
      const res = await apiService.ai.suggestPlaybook(caseId);
      setPlaybookSuggestion(res.data?.suggestion || null);
      setSuggestionVisible(true);
    } catch (err) {
      showError(err, 'Failed to get an AI playbook suggestion');
    } finally {
      setSuggestingPlaybook(false);
    }
  };

  const handleDismissSuggestion = () => setSuggestionVisible(false);

  const handleApplySuggestedPlaybook = async () => {
    if (!playbookSuggestion?.playbookId) return;
    await handleApplyPlaybook(playbookSuggestion.playbookId);
    setSuggestionVisible(false);
  };

  // ---------- AI: incident report ----------
  const buildReportMarkdown = (report) => {
    const lines = [`# AI Incident Report - ${caseData?.caseNumber || ''}`, ''];
    if (report.generatedAt || report.model) {
      lines.push(`_Generated ${report.generatedAt ? formatCaseDateTime(report.generatedAt) : ''}${report.model ? ` · ${report.model}` : ''}_`, '');
    }
    if (report.executiveSummary) lines.push('## Executive Summary', '', report.executiveSummary, '');
    if (report.technicalNarrative) lines.push('## Technical Narrative', '', report.technicalNarrative, '');
    if (report.impactAssessment) lines.push('## Impact Assessment', '', report.impactAssessment, '');
    if (report.rootCause) lines.push('## Root Cause', '', report.rootCause, '');
    if ((report.containmentActions || []).length) {
      lines.push('## Containment Actions', '', ...report.containmentActions.map((a) => `- ${a}`), '');
    }
    if ((report.recommendedNextSteps || []).length) {
      lines.push(
        '## Recommended Next Steps',
        '',
        ...report.recommendedNextSteps.map((s) => `- **[${s.priority || 'N/A'}]** ${s.action}${s.rationale ? ` — ${s.rationale}` : ''}`),
        ''
      );
    }
    if ((report.lessonsLearned || []).length) {
      lines.push('## Lessons Learned', '', ...report.lessonsLearned.map((l) => `- ${l}`), '');
    }
    if ((report.mitreTechniques || []).length) {
      lines.push('## MITRE Techniques', '', report.mitreTechniques.join(', '), '');
    }
    return lines.join('\n');
  };

  const handleGenerateReport = async () => {
    try {
      setGeneratingReport(true);
      setError('');
      const res = await apiService.ai.caseSummary(caseId);
      setCaseData((prev) => (prev ? { ...prev, aiReport: res.data?.report || prev.aiReport } : prev));
      setNotice('AI incident report generated');
    } catch (err) {
      showError(err, 'Failed to generate AI incident report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleCopyReportMarkdown = async (report) => {
    const markdown = buildReportMarkdown(report);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(markdown);
        setNotice('Report copied to clipboard as Markdown');
      } else {
        setError('Clipboard access is not available in this browser');
      }
    } catch (err) {
      showError(err, 'Failed to copy report');
    }
  };

  /** Generic PUT /cases/:id then refetch. */
  const updateCase = async (payload, successMessage) => {
    try {
      setMutating(true);
      setError('');
      await apiService.cases.update(caseId, payload);
      await fetchCase({ silent: true });
      if (successMessage) setNotice(successMessage);
      return true;
    } catch (err) {
      showError(err, 'Failed to update case');
      return false;
    } finally {
      setMutating(false);
    }
  };

  // ---------- Jira ----------
  const handleCreateJiraIssue = async () => {
    try {
      setJiraBusy(true);
      setJiraActionError('');
      const res = await apiService.integrations.createJiraIssue(caseId);
      if (res.data?.case) setCaseData(res.data.case);
      setNotice(`Jira issue ${res.data?.jira?.key || ''} created`.trim());
    } catch (err) {
      setJiraActionError(err?.response?.data?.message || 'Failed to create Jira issue');
    } finally {
      setJiraBusy(false);
    }
  };

  const handleSyncJiraIssue = async () => {
    try {
      setJiraBusy(true);
      setJiraActionError('');
      const res = await apiService.integrations.syncJiraIssue(caseId);
      if (res.data?.case) setCaseData(res.data.case);
      const newComments = res.data?.newComments || 0;
      setNotice(`Status: ${res.data?.status || 'unchanged'}, ${newComments} new comment${newComments === 1 ? '' : 's'}`);
    } catch (err) {
      showError(err, 'Failed to sync Jira issue');
    } finally {
      setJiraBusy(false);
    }
  };

  const handleUnlinkJiraIssue = async () => {
    if (!window.confirm('Unlink this case from its Jira issue? The Jira issue itself will not be deleted.')) return;
    try {
      setJiraBusy(true);
      setJiraActionError('');
      await apiService.integrations.unlinkJiraIssue(caseId);
      await fetchCase({ silent: true });
      setNotice('Jira issue unlinked');
    } catch (err) {
      showError(err, 'Failed to unlink Jira issue');
    } finally {
      setJiraBusy(false);
    }
  };

  // ---------- ServiceNow ----------
  const handleCreateSnowIncident = async () => {
    try {
      setSnowBusy(true);
      setJiraActionError('');
      const res = await apiService.integrations.createServiceNowIncident(caseId);
      if (res.data?.case) setCaseData(res.data.case);
      else await fetchCase({ silent: true });
      setNotice(`ServiceNow incident ${res.data?.servicenow?.number || ''} created`.trim());
    } catch (err) {
      setJiraActionError(err?.response?.data?.message || 'Failed to create ServiceNow incident');
    } finally {
      setSnowBusy(false);
    }
  };

  const handleSyncSnowIncident = async () => {
    try {
      setSnowBusy(true);
      setJiraActionError('');
      const res = await apiService.integrations.syncServiceNowIncident(caseId);
      if (res.data?.case) setCaseData(res.data.case);
      const newComments = res.data?.newComments || 0;
      setNotice(`State: ${res.data?.state || 'unchanged'}, ${newComments} new comment${newComments === 1 ? '' : 's'}`);
    } catch (err) {
      setJiraActionError(err?.response?.data?.message || 'Failed to sync ServiceNow incident');
    } finally {
      setSnowBusy(false);
    }
  };

  const handleUnlinkSnowIncident = async () => {
    if (!window.confirm('Unlink this case from its ServiceNow incident? The incident itself will not be deleted.')) return;
    try {
      setSnowBusy(true);
      setJiraActionError('');
      await apiService.integrations.unlinkServiceNowIncident(caseId);
      await fetchCase({ silent: true });
      setNotice('ServiceNow incident unlinked');
    } catch (err) {
      setJiraActionError(err?.response?.data?.message || 'Failed to unlink ServiceNow incident');
    } finally {
      setSnowBusy(false);
    }
  };

  // ---------- Case management ----------
  const handleEditCaseManagement = () => setEditingCaseManagement(true);

  const handleCancelCaseManagement = () => {
    setEditingCaseManagement(false);
    if (caseData) setCaseManagementForm(formFromCase(caseData));
  };

  const handleCaseManagementFormChange = (field, value) => {
    setCaseManagementForm((prev) => ({ ...prev, [field]: value }));
  };

  const resolutionMissing = () => !caseData?.resolution?.summary;

  /**
   * Save case management fields. `overrides` are merged over the current form
   * so quick actions can save immediately without waiting for state updates.
   */
  const handleSaveCaseManagement = async (overrides = {}) => {
    const form = { ...caseManagementForm, ...overrides };

    // Prompt for a resolution when moving to a resolved state without one
    if (
      RESOLVED_STATUSES.includes(form.status) &&
      !RESOLVED_STATUSES.includes(caseData?.status) &&
      resolutionMissing() &&
      !overrides.resolution
    ) {
      openResolutionDialog(form.status);
      return;
    }

    try {
      setSavingCaseManagement(true);
      setError('');
      const updateData = {
        status: form.status,
        assignedTo: form.assignedTo || '',
        severity: form.severity,
        priority: form.priority,
        category: form.category,
      };
      if (overrides.resolution) updateData.resolution = overrides.resolution;

      await apiService.cases.update(caseId, updateData);
      await fetchCase({ silent: true });
      setEditingCaseManagement(false);
      setNotice('Case updated');
    } catch (err) {
      showError(err, 'Failed to update case');
    } finally {
      setSavingCaseManagement(false);
    }
  };

  // ---------- Resolution ----------
  const openResolutionDialog = (statusToApply = null) => {
    const r = caseData?.resolution || {};
    setResolutionForm({
      summary: r.summary || '',
      rootCause: r.rootCause || '',
      actionsTaken: Array.isArray(r.actionsTaken) ? r.actionsTaken.join('\n') : (r.actionsTaken || ''),
      lessonsLearned: r.lessonsLearned || '',
    });
    setPendingStatus(statusToApply);
    setResolutionDialogOpen(true);
  };

  const closeResolutionDialog = () => {
    if (mutating || savingCaseManagement) return;
    setResolutionDialogOpen(false);
    setPendingStatus(null);
    // If the user backed out of a status change, restore the form
    if (caseData) setCaseManagementForm((prev) => ({ ...prev, status: caseData.status || prev.status }));
  };

  const handleSaveResolution = async () => {
    const existing = caseData?.resolution || {};
    const resolution = {
      ...existing,
      summary: resolutionForm.summary.trim(),
      rootCause: resolutionForm.rootCause.trim(),
      actionsTaken: resolutionForm.actionsTaken.split('\n').map((s) => s.trim()).filter(Boolean),
      lessonsLearned: resolutionForm.lessonsLearned.trim(),
      resolvedBy: refId(existing.resolvedBy) || undefined,
      resolvedAt: existing.resolvedAt || undefined,
    };
    Object.keys(resolution).forEach((k) => resolution[k] === undefined && delete resolution[k]);

    if (pendingStatus) {
      await handleSaveCaseManagement({ status: pendingStatus, resolution });
    } else {
      await updateCase({ resolution }, 'Resolution saved');
    }
    setResolutionDialogOpen(false);
    setPendingStatus(null);
  };

  // ---------- Detections ----------
  const handleViewDetection = (detectionId) => {
    setSelectedDetectionId(detectionId);
    setDetectionModalOpen(true);
  };

  const handleCloseDetectionModal = () => {
    setDetectionModalOpen(false);
    setSelectedDetectionId(null);
    fetchCase({ silent: true });
  };

  const handleAddDetection = async () => {
    if (!detectionToAdd) return;
    try {
      setMutating(true);
      setError('');
      await apiService.cases.addDetection(caseId, detectionToAdd._id);
      setDetectionToAdd(null);
      setDetectionSearch('');
      await fetchCase({ silent: true });
      setNotice('Detection linked to case');
    } catch (err) {
      showError(err, 'Failed to add detection to case');
    } finally {
      setMutating(false);
    }
  };

  const handleRemoveDetection = async (detectionId) => {
    try {
      setMutating(true);
      setError('');
      await apiService.cases.removeDetection(caseId, detectionId);
      await fetchCase({ silent: true });
      setNotice('Detection unlinked from case');
    } catch (err) {
      showError(err, 'Failed to remove detection from case');
    } finally {
      setMutating(false);
    }
  };

  // ---------- Team ----------
  const serializeTeam = (team) => (team || []).map((m) => ({
    user: refId(m.user),
    role: m.role,
    assignedAt: m.assignedAt || new Date().toISOString(),
  })).filter((m) => m.user);

  const handleAddTeamMember = async () => {
    if (!teamUser) return;
    const current = serializeTeam(caseData.team);
    if (current.some((m) => String(m.user) === String(teamUser._id))) {
      setError('That user is already on the team');
      return;
    }
    const ok = await updateCase({ team: [...current, { user: teamUser._id, role: teamRole, assignedAt: new Date().toISOString() }] }, 'Team member added');
    if (ok) setTeamUser(null);
  };

  const handleRemoveTeamMember = async (userId) => {
    const current = serializeTeam(caseData.team).filter((m) => String(m.user) !== String(userId));
    await updateCase({ team: current }, 'Team member removed');
  };

  // ---------- Tags ----------
  const handleAddTag = async () => {
    const tag = newTag.trim();
    if (!tag) return;
    const tags = caseData.tags || [];
    if (tags.includes(tag)) { setNewTag(''); return; }
    const ok = await updateCase({ tags: [...tags, tag] });
    if (ok) setNewTag('');
  };

  const handleRemoveTag = async (tag) => {
    await updateCase({ tags: (caseData.tags || []).filter((t) => t !== tag) });
  };

  // ---------- Evidence ----------
  const handleAddEvidence = async () => {
    if (!evidenceForm.name.trim()) return;
    const existing = (caseData.evidence || []).map((e) => ({ ...e, collectedBy: refId(e.collectedBy) }));
    const entry = {
      type: evidenceForm.type,
      name: evidenceForm.name.trim(),
      description: evidenceForm.description.trim(),
      collectedAt: new Date().toISOString(),
      collectedBy: user?._id,
    };
    const ok = await updateCase({ evidence: [...existing, entry] }, 'Evidence added');
    if (ok) {
      setEvidenceDialogOpen(false);
      setEvidenceForm(emptyEvidence);
    }
  };

  const handleRemoveEvidence = async (index) => {
    const existing = (caseData.evidence || []).map((e) => ({ ...e, collectedBy: refId(e.collectedBy) }));
    existing.splice(index, 1);
    await updateCase({ evidence: existing }, 'Evidence removed');
  };

  // ---------- Comments ----------
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      setAddingComment(true);
      await apiService.cases.addComment(caseId, { content: newComment.trim() });
      setNewComment('');
      await fetchCase({ silent: true });
      setError('');
    } catch (err) {
      showError(err, 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  // ---------- Delete ----------
  const handleDeleteCase = async () => {
    try {
      setDeleting(true);
      await apiService.cases.delete(caseId);
      navigate('/dashboard/cases');
    } catch (err) {
      showError(err, 'Failed to delete case');
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // ---------- Rendering helpers ----------
  const severityChip = (value, extra = {}) => {
    const color = getSeverityColor(value);
    return (
      <Chip
        label={value || '-'}
        size="small"
        sx={{ backgroundColor: color?.main || '#757575', color: color?.contrastText || '#fff', ...extra }}
      />
    );
  };

  const statusChip = (value, extra = {}) => {
    const color = getStatusColor(value);
    const c = typeof color === 'string' ? color : color?.main || '#757575';
    return <Chip label={value || '-'} size="small" variant="outlined" sx={{ borderColor: c, color: c, ...extra }} />;
  };

  const timelinePerformer = (entry) => {
    if (entry.isSystemGenerated) return 'System';
    return userName(entry.performedBy, 'System');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!caseData) {
    return (
      <Box p={3}>
        <Alert severity={error ? 'error' : 'warning'} sx={{ mb: 2 }}>
          {error || 'Case not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/cases')}>
          Back to Cases
        </Button>
      </Box>
    );
  }

  const detections = caseData.detections || [];
  const team = caseData.team || [];
  const tags = caseData.tags || [];
  const tasks = caseData.tasks || [];
  const appliedPlaybooks = caseData.playbooks || [];
  const sla = caseData.sla;
  const evidence = caseData.evidence || [];
  const indicators = (caseData.indicators || []).filter((i) => i && typeof i === 'object');
  const entities = (caseData.entities || []).filter((e) => e && typeof e === 'object');
  const resolution = caseData.resolution || null;
  const ai = caseData.aiAnalysis || null;
  const hasAi = ai && (ai.correlationScore !== undefined || ai.correlationType || ai.threatAssessment || (ai.recommendedActions || []).length > 0);
  const isAssignedToMe = user?._id && String(refId(caseData.assignedTo)) === String(user._id);
  const busy = mutating || savingCaseManagement;
  const aiReport = caseData.aiReport || null;

  const priorityChipColor = (priority) => {
    if (priority === 'Critical' || priority === 'High') return 'error';
    if (priority === 'Medium') return 'warning';
    return 'default';
  };

  return (
    <Box p={3}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component="button" variant="body1" onClick={() => navigate('/dashboard')} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <HomeIcon fontSize="small" />
          Dashboard
        </Link>
        <Link component="button" variant="body1" onClick={() => navigate('/dashboard/cases')} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CaseIcon fontSize="small" />
          Cases
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CaseIcon fontSize="small" />
          {caseData.caseNumber}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h4" gutterBottom>
            {caseData.caseNumber} - {caseData.title}
          </Typography>
          <Box display="flex" gap={1} mb={1} flexWrap="wrap">
            {severityChip(caseData.severity)}
            {statusChip(caseData.status)}
            <Chip label={`Priority: ${caseData.priority || '-'}`} size="small" variant="outlined" />
            <Chip label={caseData.category} variant="outlined" size="small" />
            <SlaBadge sla={sla} status={caseData.status} />
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <TagIcon fontSize="small" color="action" />
            {tags.length === 0 && <Typography variant="caption" color="text.secondary">No tags</Typography>}
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onDelete={canUpdate ? () => handleRemoveTag(tag) : undefined}
                disabled={busy}
              />
            ))}
            {canUpdate && (
              <TextField
                size="small"
                placeholder="Add tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                disabled={busy}
                sx={{ width: 140, '& .MuiInputBase-input': { py: 0.5, fontSize: 13 } }}
              />
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          {canDelete && (
            <Button startIcon={<DeleteIcon />} color="error" variant="outlined" onClick={() => setDeleteDialogOpen(true)}>
              Delete
            </Button>
          )}
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/cases')} variant="outlined">
            Back to Cases
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      )}
      {mutating && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} lg={8}>
          {/* Basic Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Case Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Case Number</Typography>
                  <Typography variant="body1" gutterBottom>{caseData.caseNumber}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Case ID</Typography>
                  <Typography variant="body2" gutterBottom sx={{ fontFamily: 'monospace' }}>{caseData._id}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Created</Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatCaseDateTime(caseData.createdAt)}
                    {caseData.createdBy && ` by ${userName(caseData.createdBy, 'Unknown')}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Last Updated</Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatCaseDateTime(caseData.updatedAt)}
                    {caseData.lastModifiedBy && ` by ${userName(caseData.lastModifiedBy, 'Unknown')}`}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Description */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Description</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{caseData.description}</Typography>
            </CardContent>
          </Card>

          {/* AI Incident Report */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                <Typography variant="h6" display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <ReportIcon color="primary" />
                  AI Incident Report
                  {aiReport && aiReport.confidence !== undefined && aiReport.confidence !== null && (
                    <Chip label={`${aiReport.confidence}% confidence`} size="small" color="info" />
                  )}
                </Typography>
                {canUpdate && (
                  <Tooltip title={aiStatus.available ? '' : (aiStatus.error || 'AI is not configured')}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={generatingReport ? <CircularProgress size={14} /> : <AIIcon />}
                        onClick={handleGenerateReport}
                        disabled={generatingReport || !aiStatus.available}
                      >
                        {generatingReport ? 'Generating...' : aiReport ? 'Regenerate' : 'Generate report'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>

              {aiReport && (aiReport.generatedAt || aiReport.model) && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Generated {aiReport.generatedAt ? formatCaseDateTime(aiReport.generatedAt) : 'recently'}
                  {aiReport.model ? ` · ${aiReport.model}` : ''}
                </Typography>
              )}

              {!aiStatus.available && !aiReport && (
                <Alert severity="info" sx={{ mb: 1 }}>{aiStatus.error || 'AI is not configured for this workspace.'}</Alert>
              )}

              {!aiReport ? (
                <Typography variant="body2" color="text.secondary">
                  Generate an AI incident report summarizing this case&apos;s detections, timeline, tasks, comments and threat intel.
                </Typography>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{aiReport.executiveSummary}</Typography>

                  {aiReport.technicalNarrative && (
                    <Accordion disableGutters defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2">Technical Narrative</Typography></AccordionSummary>
                      <AccordionDetails>{renderMarkdown(aiReport.technicalNarrative)}</AccordionDetails>
                    </Accordion>
                  )}

                  {aiReport.impactAssessment && (
                    <Accordion disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2">Impact Assessment</Typography></AccordionSummary>
                      <AccordionDetails>{renderMarkdown(aiReport.impactAssessment)}</AccordionDetails>
                    </Accordion>
                  )}

                  {aiReport.rootCause && (
                    <Accordion disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2">Root Cause</Typography></AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{aiReport.rootCause}</Typography>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {(aiReport.containmentActions || []).length > 0 && (
                    <Accordion disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2">Containment Actions</Typography></AccordionSummary>
                      <AccordionDetails>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {aiReport.containmentActions.map((a, i) => (
                            <li key={i}><Typography variant="body2">{a}</Typography></li>
                          ))}
                        </ul>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {(aiReport.recommendedNextSteps || []).length > 0 && (
                    <Accordion disableGutters defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2">Recommended Next Steps</Typography></AccordionSummary>
                      <AccordionDetails>
                        <List dense disablePadding>
                          {aiReport.recommendedNextSteps.map((s, i) => (
                            <ListItem key={i} disableGutters alignItems="flex-start">
                              <ListItemText primary={s.action} secondary={s.rationale} />
                              {s.priority && (
                                <Chip label={s.priority} size="small" color={priorityChipColor(s.priority)} sx={{ ml: 1 }} />
                              )}
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {(aiReport.lessonsLearned || []).length > 0 && (
                    <Accordion disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2">Lessons Learned</Typography></AccordionSummary>
                      <AccordionDetails>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {aiReport.lessonsLearned.map((l, i) => (
                            <li key={i}><Typography variant="body2">{l}</Typography></li>
                          ))}
                        </ul>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {(aiReport.mitreTechniques || []).length > 0 && (
                    <Accordion disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2">MITRE Techniques</Typography></AccordionSummary>
                      <AccordionDetails>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {aiReport.mitreTechniques.map((t, i) => <Chip key={i} label={t} size="small" variant="outlined" />)}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button size="small" startIcon={<CopyIcon />} onClick={() => handleCopyReportMarkdown(aiReport)}>
                      Copy as Markdown
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          <InvestigationTimeline
            caseId={caseData._id}
            caseNumber={caseData.caseNumber}
            canUpdate={canUpdate}
            aiAvailable={aiStatus.available}
            aiError={aiStatus.error}
            aiCode={aiStatus.code}
            notice={setNotice}
            showError={showError}
          />

          {/* Case Management */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                  <AssignmentIcon color="primary" />
                  Case Management
                </Typography>
                {canUpdate && (!editingCaseManagement ? (
                  <Button startIcon={<EditIcon />} onClick={handleEditCaseManagement} size="small" variant="outlined" disabled={busy}>
                    Edit
                  </Button>
                ) : (
                  <Box display="flex" gap={1}>
                    <Button
                      startIcon={<SaveIcon />}
                      onClick={() => handleSaveCaseManagement()}
                      size="small"
                      variant="contained"
                      disabled={savingCaseManagement}
                    >
                      {savingCaseManagement ? 'Saving...' : 'Save'}
                    </Button>
                    <Button startIcon={<CancelIcon />} onClick={handleCancelCaseManagement} size="small" variant="outlined" disabled={savingCaseManagement}>
                      Cancel
                    </Button>
                  </Box>
                ))}
              </Box>

              {!editingCaseManagement ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" gutterBottom><strong>Status:</strong></Typography>
                    <Box mb={2}>{statusChip(caseData.status)}</Box>
                    <Typography variant="body2" gutterBottom><strong>Severity:</strong></Typography>
                    <Box mb={2}>{severityChip(caseData.severity)}</Box>
                    <Typography variant="body2" gutterBottom><strong>Priority:</strong></Typography>
                    <Box mb={2}>{severityChip(caseData.priority)}</Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" gutterBottom><strong>Category:</strong></Typography>
                    <Chip label={caseData.category} variant="outlined" size="small" sx={{ mb: 2 }} />
                    <Typography variant="body2" gutterBottom><strong>Assigned To:</strong></Typography>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <PersonIcon fontSize="small" />
                      <Typography variant="body2">{userName(caseData.assignedTo)}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal" size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={caseManagementForm.status}
                        onChange={(e) => handleCaseManagementFormChange('status', e.target.value)}
                        label="Status"
                      >
                        {statusOptions.map((s) => (
                          <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal" size="small">
                      <InputLabel>Severity</InputLabel>
                      <Select
                        value={caseManagementForm.severity}
                        onChange={(e) => handleCaseManagementFormChange('severity', e.target.value)}
                        label="Severity"
                      >
                        {options.severities.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal" size="small">
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={caseManagementForm.priority}
                        onChange={(e) => handleCaseManagementFormChange('priority', e.target.value)}
                        label="Priority"
                      >
                        {options.priorities.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal" size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={options.categories.includes(caseManagementForm.category) ? caseManagementForm.category : ''}
                        onChange={(e) => handleCaseManagementFormChange('category', e.target.value)}
                        label="Category"
                      >
                        {options.categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </Select>
                    </FormControl>

                    <Autocomplete
                      options={users}
                      getOptionLabel={(option) => `${userName(option, '')}${option.username ? ` (${option.username})` : ''}`}
                      isOptionEqualToValue={(a, b) => a._id === b._id}
                      value={users.find((u) => u._id === caseManagementForm.assignedTo) || null}
                      onChange={(event, newValue) => handleCaseManagementFormChange('assignedTo', newValue?._id || '')}
                      renderInput={(params) => (
                        <TextField {...params} label="Assigned To" margin="normal" size="small" fullWidth />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option._id}>
                          <Avatar sx={{ mr: 2, width: 24, height: 24 }}>{initials(option)}</Avatar>
                          {userName(option, '')} {option.username ? `(${option.username})` : ''}
                        </Box>
                      )}
                    />
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>

          {/* Tasks */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
                <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                  <TasksIcon color="primary" />
                  Tasks ({tasks.filter((t) => t.status === 'done').length}/{tasks.length})
                </Typography>
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  {appliedPlaybooks.map((name) => (
                    <Chip key={name} icon={<PlaybookIcon />} label={name} size="small" variant="outlined" />
                  ))}
                  {canUpdate && (
                    <Button size="small" variant="outlined" startIcon={<PlaybookIcon />} onClick={() => setPlaybookPickerOpen(true)} disabled={busy || applyingPlaybook}>
                      Apply playbook
                    </Button>
                  )}
                  {canUpdate && (
                    <Tooltip title={aiStatus.available ? '' : (aiStatus.error || 'AI is not configured')}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={suggestingPlaybook ? <CircularProgress size={14} /> : <AIIcon />}
                          onClick={handleSuggestPlaybook}
                          disabled={busy || applyingPlaybook || suggestingPlaybook || !aiStatus.available}
                        >
                          {suggestingPlaybook ? 'Thinking...' : 'Suggest playbook (AI)'}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {suggestionVisible && (
                playbookSuggestion ? (
                  <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                      <Box display="flex" gap={1}>
                        <Button color="inherit" size="small" onClick={handleApplySuggestedPlaybook} disabled={applyingPlaybook}>
                          Apply {playbookSuggestion.playbook?.name || 'playbook'}
                        </Button>
                        <Button color="inherit" size="small" onClick={handleDismissSuggestion}>Dismiss</Button>
                      </Box>
                    }
                  >
                    <strong>{playbookSuggestion.playbook?.name || 'Suggested playbook'}</strong>
                    {playbookSuggestion.confidence !== undefined && playbookSuggestion.confidence !== null ? ` (${playbookSuggestion.confidence}% confidence)` : ''}
                    {playbookSuggestion.reason ? ` — ${playbookSuggestion.reason}` : ''}
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }} onClose={handleDismissSuggestion}>
                    No playbook clearly matches this case.
                  </Alert>
                )
              )}

              <TaskList
                caseId={caseId}
                tasks={tasks}
                users={users}
                canEdit={canUpdate}
                onChange={handleTasksChanged}
                onError={showError}
                onNotice={setNotice}
              />
            </CardContent>
          </Card>

          {/* Associated Detections */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <DetectionIcon color="primary" />
                Associated Detections ({detections.length})
              </Typography>

              {canUpdate && (
                <Box display="flex" gap={1} alignItems="center" mb={2}>
                  <Autocomplete
                    sx={{ flex: 1 }}
                    size="small"
                    options={detectionOptions}
                    loading={loadingDetections}
                    value={detectionToAdd}
                    onChange={(e, v) => setDetectionToAdd(v)}
                    inputValue={detectionSearch}
                    onInputChange={(e, v) => setDetectionSearch(v)}
                    filterOptions={(x) => x}
                    getOptionLabel={(d) => d.title || d._id}
                    isOptionEqualToValue={(a, b) => a._id === b._id}
                    noOptionsText={loadingDetections ? 'Searching...' : 'No unlinked detections found'}
                    renderOption={(props, d) => (
                      <Box component="li" {...props} key={d._id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {severityChip(d.severity)}
                        <Box>
                          <Typography variant="body2">{d.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {d.status}{d.category ? ` - ${d.category}` : ''}{d.source ? ` - ${d.source}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Add detection"
                        placeholder="Search unlinked detections..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingDetections ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddDetection}
                    disabled={!detectionToAdd || busy}
                  >
                    Link
                  </Button>
                </Box>
              )}

              {detections.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No detections are linked to this case yet.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Detected</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detections.map((detection) => {
                        const id = refId(detection);
                        const isDoc = detection && typeof detection === 'object';
                        return (
                          <TableRow key={id} hover>
                            <TableCell>
                              <Link
                                component="button"
                                variant="body2"
                                onClick={() => navigate(`/dashboard/detections/${id}`)}
                                sx={{ textAlign: 'left' }}
                              >
                                {isDoc ? detection.title : id}
                              </Link>
                            </TableCell>
                            <TableCell>{isDoc ? severityChip(detection.severity) : '-'}</TableCell>
                            <TableCell>{isDoc ? statusChip(detection.status) : '-'}</TableCell>
                            <TableCell>{isDoc ? formatCaseDateTime(detection.detectionTime || detection.createdAt) : '-'}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Quick view">
                                <IconButton size="small" onClick={() => handleViewDetection(id)}>
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Open detection page">
                                <IconButton size="small" onClick={() => navigate(`/dashboard/detections/${id}`)}>
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {canUpdate && (
                                <Tooltip title="Unlink from case">
                                  <span>
                                    <IconButton size="small" color="warning" onClick={() => handleRemoveDetection(id)} disabled={busy}>
                                      <UnlinkIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Indicators & Entities */}
          {(indicators.length > 0 || entities.length > 0) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                      <IndicatorIcon color="primary" />
                      Indicators ({indicators.length})
                    </Typography>
                    {indicators.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">None</Typography>
                    ) : (
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {indicators.map((ind) => {
                          const color = getSeverityColor(ind.severity);
                          return (
                            <Tooltip key={ind._id} title={`${ind.type || 'Indicator'}${ind.severity ? ` - ${ind.severity}` : ''}`}>
                              <Chip
                                label={ind.value || ind._id}
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(`/dashboard/indicators/${ind._id}`)}
                                sx={{ borderColor: color?.main, color: color?.main, fontFamily: 'monospace' }}
                              />
                            </Tooltip>
                          );
                        })}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                      <EntityIcon color="primary" />
                      Entities ({entities.length})
                    </Typography>
                    {entities.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">None</Typography>
                    ) : (
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {entities.map((ent) => (
                          <Tooltip key={ent._id} title={`${ent.type || 'Entity'}${ent.identifier ? ` - ${ent.identifier}` : ''}`}>
                            <Chip
                              label={ent.name || ent.identifier || ent._id}
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => navigate(`/dashboard/entities/${ent._id}`)}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* AI Analysis */}
          {hasAi && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                  <AIIcon color="primary" />
                  AI Analysis
                </Typography>
                <Grid container spacing={2}>
                  {ai.correlationScore !== undefined && ai.correlationScore !== null && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Correlation Score</Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box flex={1}>
                          <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, Number(ai.correlationScore) || 0))} sx={{ height: 8, borderRadius: 4 }} />
                        </Box>
                        <Typography variant="body2" fontWeight="medium">{ai.correlationScore}%</Typography>
                      </Box>
                    </Grid>
                  )}
                  {ai.correlationType && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Correlation Type</Typography>
                      <Chip label={ai.correlationType} size="small" variant="outlined" />
                    </Grid>
                  )}
                  {ai.threatAssessment && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Threat Assessment</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {typeof ai.threatAssessment === 'string' ? ai.threatAssessment : JSON.stringify(ai.threatAssessment, null, 2)}
                      </Typography>
                    </Grid>
                  )}
                  {(ai.recommendedActions || []).length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Recommended Actions</Typography>
                      <List dense disablePadding>
                        {ai.recommendedActions.map((a, i) => (
                          <ListItem key={i} disableGutters>
                            <ListItemText
                              primary={a.action}
                              secondary={[a.priority ? `Priority: ${a.priority}` : null, a.confidence !== undefined ? `Confidence: ${a.confidence}%` : null].filter(Boolean).join(' | ')}
                            />
                            {a.priority && severityChip(a.priority)}
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Resolution */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                  <ResolvedIcon color={resolution?.summary ? 'success' : 'disabled'} />
                  Resolution
                </Typography>
                {canUpdate && (
                  <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openResolutionDialog(null)} disabled={busy}>
                    {resolution?.summary ? 'Edit' : 'Add'}
                  </Button>
                )}
              </Box>
              {!resolution || (!resolution.summary && !resolution.rootCause && !resolution.resolvedAt) ? (
                <Typography variant="body2" color="text.secondary">
                  No resolution recorded. You will be prompted for one when marking the case Resolved or Closed.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Summary</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{resolution.summary || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Root Cause</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{resolution.rootCause || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Lessons Learned</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{resolution.lessonsLearned || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Actions Taken</Typography>
                    {(resolution.actionsTaken || []).length === 0 ? (
                      <Typography variant="body2">-</Typography>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {resolution.actionsTaken.map((a, i) => (
                          <li key={i}><Typography variant="body2">{a}</Typography></li>
                        ))}
                      </ul>
                    )}
                  </Grid>
                  {(resolution.resolvedBy || resolution.resolvedAt) && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Resolved {resolution.resolvedAt ? formatCaseDateTime(resolution.resolvedAt) : ''}
                        {resolution.resolvedBy ? ` by ${userName(resolution.resolvedBy, 'Unknown')}` : ''}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </CardContent>
          </Card>

          {/* Evidence */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                  <EvidenceIcon color="primary" />
                  Evidence ({evidence.length})
                </Typography>
                {canUpdate && (
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setEvidenceDialogOpen(true)} disabled={busy}>
                    Add
                  </Button>
                )}
              </Box>
              {evidence.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No evidence recorded.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Collected</TableCell>
                        {canUpdate && <TableCell align="right" />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {evidence.map((ev, i) => (
                        <TableRow key={ev._id || i} hover>
                          <TableCell><Chip label={ev.type || 'Other'} size="small" variant="outlined" /></TableCell>
                          <TableCell>{ev.name}</TableCell>
                          <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{ev.description || '-'}</TableCell>
                          <TableCell>
                            {ev.collectedAt ? formatCaseDateTime(ev.collectedAt) : '-'}
                            {ev.collectedBy && typeof ev.collectedBy === 'object' ? ` by ${userName(ev.collectedBy, '')}` : ''}
                          </TableCell>
                          {canUpdate && (
                            <TableCell align="right">
                              <Tooltip title="Remove evidence">
                                <span>
                                  <IconButton size="small" onClick={() => handleRemoveEvidence(i)} disabled={busy}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Quick Actions */}
          {canUpdate && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Quick Actions</Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {caseData.status === 'Open' && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleSaveCaseManagement({ status: 'In Progress' })}
                      disabled={busy}
                      fullWidth
                    >
                      Start Investigation
                    </Button>
                  )}
                  {['In Progress', 'Under Review'].includes(caseData.status) && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => handleSaveCaseManagement({ status: 'Resolved' })}
                      disabled={busy}
                      fullWidth
                    >
                      Mark Resolved
                    </Button>
                  )}
                  {caseData.status === 'Resolved' && (
                    <Button
                      variant="outlined"
                      onClick={() => handleSaveCaseManagement({ status: 'Closed' })}
                      disabled={busy}
                      fullWidth
                    >
                      Close Case
                    </Button>
                  )}
                  {RESOLVED_STATUSES.includes(caseData.status) && (
                    <Button
                      variant="outlined"
                      onClick={() => handleSaveCaseManagement({ status: 'In Progress' })}
                      disabled={busy}
                      fullWidth
                    >
                      Reopen Case
                    </Button>
                  )}
                  {!isAssignedToMe && user?._id && (
                    <Button
                      variant="outlined"
                      startIcon={<PersonIcon />}
                      onClick={() => handleSaveCaseManagement({ assignedTo: user._id })}
                      disabled={busy}
                      fullWidth
                    >
                      Assign to Me
                    </Button>
                  )}
                  {caseData.status === 'Open' && isAssignedToMe && (
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                      This case is assigned to you.
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* SLA */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <SlaIcon color="primary" />
                SLA
              </Typography>
              {!sla || sla.enabled === false ? (
                <Typography variant="body2" color="text.secondary">SLA tracking is disabled for this case.</Typography>
              ) : (
                <Box>
                  <Box mb={2}><SlaBadge sla={sla} status={caseData.status} /></Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Target</TableCell>
                          <TableCell>Due</TableCell>
                          <TableCell>Actual</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[
                          { key: 'response', label: 'Response', minutes: sla.responseMinutes, by: sla.respondBy, at: sla.respondedAt, breached: sla.responseBreached },
                          { key: 'resolution', label: 'Resolution', minutes: sla.resolutionMinutes, by: sla.resolveBy, at: sla.resolvedAt, breached: sla.resolutionBreached },
                        ].map((row) => {
                          const by = toDate(row.by);
                          const at = toDate(row.at);
                          return (
                            <TableRow key={row.key}>
                              <TableCell>
                                <Typography variant="body2">{row.label}</Typography>
                                <Typography variant="caption" color="text.secondary">{minutesToHuman(row.minutes)} from creation</Typography>
                              </TableCell>
                              <TableCell><Typography variant="body2">{by ? formatCaseDateTime(by) : '-'}</Typography></TableCell>
                              <TableCell>
                                <Typography variant="body2">{at ? formatCaseDateTime(at) : 'Pending'}</Typography>
                                {row.breached ? (
                                  <Chip label={at && by ? `Breached by ${formatDuration(at - by)}` : 'Breached'} size="small" color="error" sx={{ mt: 0.5 }} />
                                ) : at && by ? (
                                  <Chip label={`Met with ${formatDuration(by - at)} to spare`} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                                ) : null}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {sla.breached && (
                    <Alert severity="error" sx={{ mt: 2 }} variant="outlined">This case has breached its SLA.</Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Team */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <TeamIcon color="primary" />
                Team ({team.length})
              </Typography>
              {team.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No team members yet.</Typography>
              ) : (
                <List dense disablePadding sx={{ mb: 1 }}>
                  {team.map((m, i) => {
                    const uid = refId(m.user);
                    return (
                      <ListItem
                        key={uid || i}
                        disableGutters
                        secondaryAction={canUpdate ? (
                          <Tooltip title="Remove from team">
                            <span>
                              <IconButton edge="end" size="small" onClick={() => handleRemoveTeamMember(uid)} disabled={busy}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ width: 32, height: 32 }}>{initials(m.user)}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={userName(m.user, 'Unknown user')}
                          secondary={`${m.role || 'Member'}${m.assignedAt ? ` - since ${formatCaseDateTime(m.assignedAt)}` : ''}`}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
              {canUpdate && (
                <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap">
                  <Autocomplete
                    sx={{ flex: 1, minWidth: 160 }}
                    size="small"
                    options={users.filter((u) => !team.some((m) => String(refId(m.user)) === String(u._id)))}
                    getOptionLabel={(o) => userName(o, '')}
                    isOptionEqualToValue={(a, b) => a._id === b._id}
                    value={teamUser}
                    onChange={(e, v) => setTeamUser(v)}
                    renderInput={(params) => <TextField {...params} label="Add member" size="small" />}
                  />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Role</InputLabel>
                    <Select value={teamRole} label="Role" onChange={(e) => setTeamRole(e.target.value)}>
                      {options.teamRoles.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button variant="contained" size="small" onClick={handleAddTeamMember} disabled={!teamUser || busy} sx={{ height: 40 }}>
                    Add
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* External tickets */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <JiraIcon color="primary" />
                External tickets
              </Typography>
              {jiraActionError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJiraActionError('')}>
                  {jiraActionError}
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Configure the ticketing integrations under Settings → System Integrations.
                  </Typography>
                </Alert>
              )}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Jira</Typography>
              {caseData.external?.jira?.key ? (
                <Box>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                    <Link
                      href={caseData.external.jira.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {caseData.external.jira.key}
                      <OpenInNewIcon fontSize="inherit" />
                    </Link>
                    <Chip
                      size="small"
                      label={caseData.external.jira.status || 'Unknown'}
                      color={
                        caseData.external.jira.statusCategory === 'done' ? 'success'
                          : caseData.external.jira.statusCategory === 'indeterminate' ? 'warning'
                            : 'default'
                      }
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Assignee: {caseData.external.jira.assignee || 'Unassigned'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Last synced: {caseData.external.jira.lastSyncedAt ? formatRelativeTime(caseData.external.jira.lastSyncedAt) : 'Never'}
                  </Typography>
                  {canUpdate && (
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={jiraBusy ? <CircularProgress size={14} /> : <SyncIcon />}
                        onClick={handleSyncJiraIssue}
                        disabled={jiraBusy}
                      >
                        Sync now
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<UnlinkIcon />}
                        onClick={handleUnlinkJiraIssue}
                        disabled={jiraBusy}
                      >
                        Unlink
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    This case is not linked to a Jira issue.
                  </Typography>
                  {canUpdate && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={jiraBusy ? <CircularProgress size={14} /> : <AddIcon />}
                      onClick={handleCreateJiraIssue}
                      disabled={jiraBusy}
                    >
                      Create Jira issue
                    </Button>
                  )}
                </Box>
              )}

              {(snowEnabled || caseData.external?.servicenow?.number) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>ServiceNow</Typography>
                  {caseData.external?.servicenow?.number ? (
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                        <Link
                          href={caseData.external.servicenow.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          {caseData.external.servicenow.number}
                          <OpenInNewIcon fontSize="inherit" />
                        </Link>
                        <Chip
                          size="small"
                          label={caseData.external.servicenow.stateLabel || caseData.external.servicenow.state || 'Unknown'}
                          color={
                            /closed|resolved/i.test(caseData.external.servicenow.stateLabel || '') ? 'success'
                              : /progress|hold|pending/i.test(caseData.external.servicenow.stateLabel || '') ? 'warning'
                                : 'default'
                          }
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Assigned to: {caseData.external.servicenow.assignedTo || 'Unassigned'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Last synced: {caseData.external.servicenow.lastSyncedAt ? formatRelativeTime(caseData.external.servicenow.lastSyncedAt) : 'Never'}
                      </Typography>
                      {canUpdate && (
                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={snowBusy ? <CircularProgress size={14} /> : <SyncIcon />}
                            onClick={handleSyncSnowIncident}
                            disabled={snowBusy}
                          >
                            Sync now
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<UnlinkIcon />}
                            onClick={handleUnlinkSnowIncident}
                            disabled={snowBusy}
                          >
                            Unlink
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        This case is not linked to a ServiceNow incident.
                      </Typography>
                      {canUpdate && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={snowBusy ? <CircularProgress size={14} /> : <AddIcon />}
                          onClick={handleCreateSnowIncident}
                          disabled={snowBusy}
                        >
                          Create ServiceNow incident
                        </Button>
                      )}
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          {caseData.timeline && caseData.timeline.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                  <TimelineIcon color="primary" />
                  Timeline
                </Typography>
                <List dense>
                  {caseData.timeline.map((entry, index) => (
                    <ListItem key={entry._id || index} alignItems="flex-start" disableGutters>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                          {entry.isSystemGenerated ? 'S' : initials(entry.performedBy)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={entry.action}
                        secondary={
                          <>
                            <Typography component="span" variant="caption" color="text.secondary" display="block">
                              {formatCaseDateTime(entry.timestamp)} - {timelinePerformer(entry)}
                            </Typography>
                            {entry.description && (
                              <Typography component="span" variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {entry.description}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {/* Comments */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <CommentIcon color="primary" />
                Comments ({caseData.comments?.length || 0})
              </Typography>

              {caseData.comments && caseData.comments.length > 0 ? (
                <List sx={{ mb: 2 }}>
                  {caseData.comments.map((comment, index) => (
                    <ListItem key={comment._id || index} alignItems="flex-start" disableGutters>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32 }}>{initials(comment.author)}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2">{userName(comment.author, 'Unknown User')}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatCaseDateTime(comment.createdAt)}</Typography>
                          </Box>
                        }
                        secondary={
                          <Typography component="span" variant="body2" display="block" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                            {comment.content}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No comments yet</Typography>
              )}

              {canUpdate && (
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      startIcon={<SendIcon />}
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || addingComment}
                      variant="contained"
                      size="small"
                    >
                      {addingComment ? 'Adding...' : 'Add'}
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          <AskAiPanel
            targetType="case"
            targetId={caseData._id}
            aiAvailable={aiStatus.available}
            aiError={aiStatus.error}
            aiCode={aiStatus.code}
            compact
          />
        </Grid>
      </Grid>

      {/* Detection Detail Modal */}
      {selectedDetectionId && (
        <DetectionDetail
          open={detectionModalOpen}
          onClose={handleCloseDetectionModal}
          detectionId={selectedDetectionId}
        />
      )}

      {/* Resolution Dialog */}
      <Dialog open={resolutionDialogOpen} onClose={closeResolutionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {pendingStatus ? `Mark case as ${pendingStatus}` : 'Case Resolution'}
        </DialogTitle>
        <DialogContent>
          {pendingStatus && (
            <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
              Record how this case was resolved before changing its status to {pendingStatus}.
            </Alert>
          )}
          <TextField
            fullWidth
            required
            margin="normal"
            label="Summary"
            multiline
            rows={3}
            value={resolutionForm.summary}
            onChange={(e) => setResolutionForm((p) => ({ ...p, summary: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Root Cause"
            multiline
            rows={2}
            value={resolutionForm.rootCause}
            onChange={(e) => setResolutionForm((p) => ({ ...p, rootCause: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Actions Taken (one per line)"
            multiline
            rows={3}
            value={resolutionForm.actionsTaken}
            onChange={(e) => setResolutionForm((p) => ({ ...p, actionsTaken: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Lessons Learned"
            multiline
            rows={2}
            value={resolutionForm.lessonsLearned}
            onChange={(e) => setResolutionForm((p) => ({ ...p, lessonsLearned: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResolutionDialog} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveResolution}
            disabled={busy || !resolutionForm.summary.trim()}
          >
            {busy ? 'Saving...' : pendingStatus ? `Save & mark ${pendingStatus}` : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Evidence Dialog */}
      <Dialog open={evidenceDialogOpen} onClose={() => !busy && setEvidenceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Evidence</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>Type</InputLabel>
            <Select value={evidenceForm.type} label="Type" onChange={(e) => setEvidenceForm((p) => ({ ...p, type: e.target.value }))}>
              {EVIDENCE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            required
            margin="normal"
            size="small"
            label="Name"
            value={evidenceForm.name}
            onChange={(e) => setEvidenceForm((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            size="small"
            label="Description"
            multiline
            rows={3}
            value={evidenceForm.description}
            onChange={(e) => setEvidenceForm((p) => ({ ...p, description: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvidenceDialogOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={handleAddEvidence} disabled={busy || !evidenceForm.name.trim()}>
            {busy ? 'Saving...' : 'Add Evidence'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply playbook */}
      <PlaybookPicker
        open={playbookPickerOpen}
        onClose={() => setPlaybookPickerOpen(false)}
        onApply={handleApplyPlaybook}
        applied={appliedPlaybooks}
        applying={applyingPlaybook}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete case {caseData.caseNumber}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This permanently deletes the case and unlinks its {detections.length} detection{detections.length === 1 ? '' : 's'}. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteCase} disabled={deleting} startIcon={<DeleteIcon />}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseDetailPage;
